import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star, FileText, ExternalLink, Trash2, Users, TrendingUp, Award, Eye, X, Heart, MessageCircle, Calendar, Hash, Euro, MapPin, Building2, User, Phone, Mail, Briefcase, Folder, Share2, Plus, Play, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';
import InfluencerAvatar from '../components/InfluencerAvatar';
import { Collab, getDocuments as getSupabaseDocuments } from '../lib/supabaseHelpers';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const YouTubeIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const SnapchatIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.166 2C9.036 2 7.07 3.686 6.326 5.956c-.199.6-.165 1.817-.148 2.535l-.017.003c-.27.044-.57.028-.88-.02-.277-.044-.54-.11-.77-.11-.43 0-.814.168-.99.5-.13.244-.13.524.02.784.31.544 1.05.946 2.09 1.134.014.006.025.01.038.016-.016.053-.033.107-.05.162-.2.65-.557 1.24-1.09 1.617-.43.302-.644.782-.547 1.252.08.393.38.72.81.876.246.089.53.128.842.114.215-.01.44-.04.672-.082.383.636 1.044 1.082 2.098 1.266.88.153 1.91.128 2.72.12h.008c.81.008 1.84.033 2.72-.12 1.054-.184 1.715-.63 2.098-1.266.232.042.457.072.672.082.312.014.596-.025.842-.114.43-.156.73-.483.81-.876.097-.47-.117-.95-.547-1.252-.533-.377-.89-.966-1.09-1.617a5.49 5.49 0 01-.05-.162c.013-.006.024-.01.038-.016 1.04-.188 1.78-.59 2.09-1.134.15-.26.15-.54.02-.784-.176-.332-.56-.5-.99-.5-.23 0-.493.066-.77.11-.31.048-.61.064-.88.02l-.017-.003c.017-.718.051-1.935-.148-2.535C16.93 3.686 14.964 2 11.834 2z"/>
  </svg>
);

interface InfluencerData {
  id: string;
  name: string | null;
  display_name: string | null;
  username: string;
  platform: string;
  followers_count: number;
  engagement_rate: number;
  category: string | null;
  profile_image_url: string | null;
  profile_url: string | null;
  bio: string | null;
  verified: boolean;
  avg_likes: number;
  avg_comments: number;
  posts_count: number;
  country: string | null;
}

interface OrganizationInfluencer {
  id: string;
  influencer_id: string;
  notes: string;
  tags: string[];
  rating: number | null;
  added_at: string;
  influencer: InfluencerData;
  collabCount?: number;
  first_name?: string;
  last_name?: string;
  address?: string;
  siret?: string;
  date_of_birth?: string;
  pricing?: Record<string, number>;
  manager_name?: string;
  manager_email?: string;
  manager_phone?: string;
  manager_company?: string;
}

interface Post {
  id: string;
  post_id: string;
  post_url: string;
  post_type: string;
  caption: string;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  timestamp: string | null;
  is_collaboration: boolean;
  collaboration_keywords: string[];
  scraped_at: string;
}

interface TikTokProfile {
  tiktok_username: string;
  display_name: string | null;
  followers_count: number;
  following_count: number;
  likes_count: number;
  videos_count: number;
  bio: string | null;
  profile_image_url: string | null;
  verified: boolean;
  avg_views: number;
  avg_likes: number;
  avg_comments: number;
  engagement_rate: number | null;
  scraped_at: string;
}

interface TikTokPost {
  id: string;
  video_id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  description: string | null;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  views_count: number;
  duration: number | null;
  timestamp: string | null;
}

interface YouTubeProfile {
  youtube_handle: string;
  channel_id: string | null;
  channel_name: string | null;
  subscribers_count: number;
  total_views: number;
  total_videos: number;
  description: string | null;
  avatar_url: string | null;
  avg_views: number;
  avg_likes: number;
  avg_comments: number;
  engagement_rate: number | null;
  scraped_at: string;
}

interface YouTubeVideo {
  id: string;
  video_id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  title: string | null;
  view_count: number;
  likes_count: number;
  comments_count: number;
  duration: string | null;
  published_at: string | null;
}

interface SnapchatProfile {
  username: string;
  display_name: string | null;
  name: string | null;
  account_type: string;
  profile_picture_url: string | null;
  bio: string | null;
  location: string | null;
  category: string | null;
  website_url: string | null;
  profile_url: string | null;
  snapcode_url: string | null;
  subscriber_count: number;
  scraped_at: string;
}

interface SnapchatSnap {
  id: string;
  snap_url: string | null;
  thumbnail_url: string | null;
  upload_timestamp: string | null;
  media_type: string;
  source: string;
}

interface SnapchatStory {
  id: string;
  story_title: string | null;
  thumbnail_url: string | null;
  snaps: Array<{ mediaUrl?: string; previewUrl?: string; timestamp?: string }>;
}

interface SnapchatSpotlight {
  id: string;
  title: string | null;
  description: string | null;
  views: number;
  thumbnail_url: string | null;
  hashtags: string[];
  snaps: Array<{ mediaUrl?: string; previewUrl?: string; timestamp?: string }>;
}

export default function Influencers() {
  const { currentUser, userProfile } = useAuth();
  const [organizationInfluencers, setOrganizationInfluencers] = useState<OrganizationInfluencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedInfluencer, setSelectedInfluencer] = useState<OrganizationInfluencer | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [editingRating, setEditingRating] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [editingData, setEditingData] = useState<{
    first_name: string;
    last_name: string;
    address: string;
    siret: string;
    date_of_birth: string;
    pricing: Record<string, string>;
    manager_name: string;
    manager_email: string;
    manager_phone: string;
    manager_company: string;
  }>({
    first_name: '',
    last_name: '',
    address: '',
    siret: '',
    date_of_birth: '',
    pricing: {},
    manager_name: '',
    manager_email: '',
    manager_phone: '',
    manager_company: ''
  });
  const [collabPosts, setCollabPosts] = useState<Post[]>([]);
  const [loadingCollabPosts, setLoadingCollabPosts] = useState(false);
  const [organizationCollabs, setOrganizationCollabs] = useState<Collab[]>([]);
  const [loadingOrgCollabs, setLoadingOrgCollabs] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'by-member'>('by-member');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [memberFilter, setMemberFilter] = useState<string>('all');
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  // TikTok states
  const [tiktokInputMap, setTiktokInputMap] = useState<Record<string, string>>({});
  const [tiktokScraping, setTiktokScraping] = useState<Record<string, boolean>>({});
  const [tiktokProfiles, setTiktokProfiles] = useState<Record<string, TikTokProfile>>({});
  const [tiktokPosts, setTiktokPosts] = useState<Record<string, TikTokPost[]>>({});
  const [platformTab, setPlatformTab] = useState<Record<string, 'instagram' | 'tiktok' | 'youtube' | 'snapchat'>>({});
  const [showTiktokInput, setShowTiktokInput] = useState<Record<string, boolean>>({});
  const [modalPlatformTab, setModalPlatformTab] = useState<'instagram' | 'tiktok' | 'youtube' | 'snapchat'>('instagram');
  const [loadingTiktokPosts, setLoadingTiktokPosts] = useState(false);

  // YouTube states
  const [youtubeInputMap, setYoutubeInputMap] = useState<Record<string, string>>({});
  const [youtubeScraping, setYoutubeScraping] = useState<Record<string, boolean>>({});
  const [youtubeProfiles, setYoutubeProfiles] = useState<Record<string, YouTubeProfile>>({});
  const [youtubeVideos, setYoutubeVideos] = useState<Record<string, YouTubeVideo[]>>({});
  const [showYoutubeInput, setShowYoutubeInput] = useState<Record<string, boolean>>({});

  // Snapchat states
  const [snapchatInputMap, setSnapchatInputMap] = useState<Record<string, string>>({});
  const [snapchatScraping, setSnapchatScraping] = useState<Record<string, boolean>>({});
  const [snapchatProfiles, setSnapchatProfiles] = useState<Record<string, SnapchatProfile>>({});
  const [snapchatSnaps, setSnapchatSnaps] = useState<Record<string, SnapchatSnap[]>>({});
  const [snapchatStories, setSnapchatStories] = useState<Record<string, SnapchatStory[]>>({});
  const [snapchatSpotlights, setSnapchatSpotlights] = useState<Record<string, SnapchatSpotlight[]>>({});
  const [showSnapchatInput, setShowSnapchatInput] = useState<Record<string, boolean>>({});
  const [refreshingAll, setRefreshingAll] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (currentUser && userProfile?.agency_id) {
      loadOrganizationInfluencers();
    }
  }, [userProfile?.agency_id]);

  const loadOrganizationInfluencers = async () => {
    if (!currentUser || !userProfile?.agency_id) {
      console.log('❌ No user or agency_id:', { currentUser: !!currentUser, agency_id: userProfile?.agency_id });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('🔍 Loading influencers for agency:', userProfile.agency_id);

      const { data, error } = await supabase
        .from('organization_influencers')
        .select(`
          *,
          influencer:influencers(*)
        `)
        .eq('organization_id', userProfile.agency_id)
        .order('added_at', { ascending: false });

      console.log('📊 Query result:', { data, error });

      if (error) throw error;

      const userIds = [...new Set((data || []).map((inf: any) => inf.added_by).filter(Boolean))];

      let usersMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .in('id', userIds);

        if (!usersError && usersData) {
          usersMap = Object.fromEntries(usersData.map(u => [u.id, u]));
        }
        console.log('👥 Users data:', usersMap);
      }

      const collabs = await getSupabaseDocuments<Collab>('collabs', userProfile.agency_id);

      const influencersWithCollabs = (data || []).map((orgInf: any) => {
        const influencer = orgInf.influencer;

        const collabCount = collabs.filter((collab: Collab) =>
          collab.influencer_ids?.includes(orgInf.influencer_id) ||
          collab.influencers?.some((inf: any) => inf.id === orgInf.influencer_id)
        ).length;

        const addedByUser = orgInf.added_by ? usersMap[orgInf.added_by] : null;
        const userName = addedByUser
          ? `${addedByUser.first_name || ''} ${addedByUser.last_name || ''}`.trim() || addedByUser.email
          : 'Utilisateur inconnu';

        return {
          ...orgInf,
          collabCount,
          added_by_user_id: orgInf.added_by,
          added_by_name: userName
        };
      });

      console.log('✅ Final influencers:', influencersWithCollabs);
      setOrganizationInfluencers(influencersWithCollabs);
      const ids = influencersWithCollabs.map((inf: any) => inf.influencer_id).filter(Boolean);
      await Promise.all([loadTiktokData(ids), loadYoutubeData(ids), loadSnapchatData(ids)]);
    } catch (error) {
      console.error('❌ Error loading organization influencers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTiktokData = async (influencerIds: string[]) => {
    if (influencerIds.length === 0) return;
    const { data: profiles } = await supabase
      .from('tiktok_profiles')
      .select('*')
      .in('influencer_id', influencerIds);
    if (profiles) {
      const map: Record<string, TikTokProfile> = {};
      for (const p of profiles) map[p.influencer_id] = p;
      setTiktokProfiles(map);
    }
    const { data: posts } = await supabase
      .from('tiktok_posts')
      .select('*')
      .in('influencer_id', influencerIds)
      .order('timestamp', { ascending: false });
    if (posts) {
      const map: Record<string, TikTokPost[]> = {};
      for (const p of posts) {
        if (!map[p.influencer_id]) map[p.influencer_id] = [];
        map[p.influencer_id].push(p);
      }
      setTiktokPosts(map);
    }
  };

  const loadYoutubeData = async (influencerIds: string[]) => {
    if (influencerIds.length === 0) return;
    const { data: profiles } = await supabase
      .from('youtube_profiles')
      .select('*')
      .in('influencer_id', influencerIds);
    if (profiles) {
      const map: Record<string, YouTubeProfile> = {};
      for (const p of profiles) map[p.influencer_id] = p;
      setYoutubeProfiles(map);
    }
    const { data: videos } = await supabase
      .from('youtube_videos')
      .select('*')
      .in('influencer_id', influencerIds)
      .order('published_at', { ascending: false });
    if (videos) {
      const map: Record<string, YouTubeVideo[]> = {};
      for (const v of videos) {
        if (!map[v.influencer_id]) map[v.influencer_id] = [];
        map[v.influencer_id].push(v);
      }
      setYoutubeVideos(map);
    }
  };

  const loadSnapchatData = async (influencerIds: string[]) => {
    if (influencerIds.length === 0) return;
    const { data: profiles } = await supabase
      .from('snapchat_profiles')
      .select('*')
      .in('influencer_id', influencerIds);
    if (profiles) {
      const map: Record<string, SnapchatProfile> = {};
      for (const p of profiles) map[p.influencer_id] = p;
      setSnapchatProfiles(map);
    }
    const { data: stories } = await supabase
      .from('snapchat_stories')
      .select('*')
      .in('influencer_id', influencerIds)
      .order('created_at', { ascending: false });
    if (stories) {
      const map: Record<string, SnapchatStory[]> = {};
      for (const s of stories) {
        if (!map[s.influencer_id]) map[s.influencer_id] = [];
        map[s.influencer_id].push(s);
      }
      setSnapchatStories(map);
    }
    const { data: spotlights } = await supabase
      .from('snapchat_spotlights')
      .select('*')
      .in('influencer_id', influencerIds)
      .order('views', { ascending: false });
    if (spotlights) {
      const map: Record<string, SnapchatSpotlight[]> = {};
      for (const s of spotlights) {
        if (!map[s.influencer_id]) map[s.influencer_id] = [];
        map[s.influencer_id].push(s);
      }
      setSnapchatSpotlights(map);
    }
  };

  const handleScrapeSnapchat = async (orgInf: OrganizationInfluencer) => {
    const handle = snapchatInputMap[orgInf.id]?.trim();
    if (!handle) return;
    setSnapchatScraping(prev => ({ ...prev, [orgInf.id]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/snapchat-scraper`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ influencerId: orgInf.influencer_id, snapchatUsername: handle }),
        }
      );
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'snapchat' }));
      setShowSnapchatInput(prev => ({ ...prev, [orgInf.id]: false }));
      await loadSnapchatData([orgInf.influencer_id]);
      // If still pending (background job), poll until data appears
      if (result.pending) {
        let attempts = 0;
        const poll = async () => {
          attempts++;
          await loadSnapchatData([orgInf.influencer_id]);
          // Check if data arrived (subscriber_count > 0 means something came back)
          // We just reload up to 10 times every 8s
          if (attempts < 10) setTimeout(poll, 8000);
        };
        setTimeout(poll, 8000);
      }
    } catch (err) {
      console.error('Snapchat scraping error:', err);
      alert('Erreur lors du scraping Snapchat: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSnapchatScraping(prev => ({ ...prev, [orgInf.id]: false }));
    }
  };

  const handleScrapeYoutube = async (orgInf: OrganizationInfluencer) => {
    const handle = youtubeInputMap[orgInf.id]?.trim();
    if (!handle) return;
    setYoutubeScraping(prev => ({ ...prev, [orgInf.id]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/youtube-scraper`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ influencerId: orgInf.influencer_id, youtubeHandle: handle }),
        }
      );
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      await loadYoutubeData([orgInf.influencer_id]);
      setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'youtube' }));
      setShowYoutubeInput(prev => ({ ...prev, [orgInf.id]: false }));
    } catch (err) {
      console.error('YouTube scraping error:', err);
      alert('Erreur lors du scraping YouTube: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setYoutubeScraping(prev => ({ ...prev, [orgInf.id]: false }));
    }
  };

  const handleScrapeTiktok = async (orgInf: OrganizationInfluencer) => {
    const handle = tiktokInputMap[orgInf.id]?.trim();
    if (!handle) return;
    setTiktokScraping(prev => ({ ...prev, [orgInf.id]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tiktok-scraper`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ influencerId: orgInf.influencer_id, tiktokHandle: handle }),
        }
      );
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      await loadTiktokData([orgInf.influencer_id]);
      setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'tiktok' }));
      setShowTiktokInput(prev => ({ ...prev, [orgInf.id]: false }));
    } catch (err) {
      console.error('TikTok scraping error:', err);
      alert('Erreur lors du scraping TikTok: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setTiktokScraping(prev => ({ ...prev, [orgInf.id]: false }));
    }
  };

  const handleRefreshAll = async (orgInf: OrganizationInfluencer) => {
    const influencer = orgInf.influencer;
    const ttProfile = tiktokProfiles[orgInf.influencer_id];
    const ytProfile = youtubeProfiles[orgInf.influencer_id];
    const snapProfile = snapchatProfiles[orgInf.influencer_id];
    const hasInstagram = influencer?.platform === 'instagram' && influencer?.username;

    if (!ttProfile && !ytProfile && !snapProfile && !hasInstagram) return;

    setRefreshingAll(prev => ({ ...prev, [orgInf.id]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`,
      };
      const anonHeaders = {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
      };
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;

      const jobs: Promise<void>[] = [];

      if (hasInstagram) {
        const profileUrl = `https://www.instagram.com/${influencer.username}/`;
        jobs.push(
          fetch(`${baseUrl}/functions/v1/instagram-scraper`, {
            method: 'POST', headers: anonHeaders,
            body: JSON.stringify({ instagramUrls: [profileUrl], resultsType: 'posts', resultsLimit: 200 }),
          })
            .then(r => r.json())
            .then(result => {
              if (result.success && result.influencers?.[0]) {
                return fetch(`${baseUrl}/functions/v1/scrape-influencer-posts`, {
                  method: 'POST', headers: anonHeaders,
                  body: JSON.stringify({
                    influencerId: result.influencers[0].id,
                    username: result.influencers[0].username,
                    resultsLimit: 15,
                  }),
                }).then(() => {});
              }
            })
            .catch(err => console.error('Instagram refresh error:', err))
        );
      }

      if (ttProfile) {
        jobs.push(
          fetch(`${baseUrl}/functions/v1/tiktok-scraper`, {
            method: 'POST', headers: authHeaders,
            body: JSON.stringify({ influencerId: orgInf.influencer_id, tiktokHandle: ttProfile.tiktok_username }),
          }).then(() => loadTiktokData([orgInf.influencer_id]))
        );
      }

      if (ytProfile) {
        jobs.push(
          fetch(`${baseUrl}/functions/v1/youtube-scraper`, {
            method: 'POST', headers: authHeaders,
            body: JSON.stringify({ influencerId: orgInf.influencer_id, youtubeHandle: ytProfile.youtube_handle }),
          }).then(() => loadYoutubeData([orgInf.influencer_id]))
        );
      }

      if (snapProfile) {
        jobs.push(
          fetch(`${baseUrl}/functions/v1/snapchat-scraper`, {
            method: 'POST', headers: authHeaders,
            body: JSON.stringify({ influencerId: orgInf.influencer_id, snapchatUsername: snapProfile.username }),
          }).then(() => loadSnapchatData([orgInf.influencer_id]))
        );
      }

      await Promise.allSettled(jobs);
    } catch (err) {
      console.error('Refresh all error:', err);
    } finally {
      setRefreshingAll(prev => ({ ...prev, [orgInf.id]: false }));
    }
  };

  const handleRemoveInfluencer = async (id: string) => {
    try {
      const { error } = await supabase
        .from('organization_influencers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setOrganizationInfluencers(prev => prev.filter(inf => inf.id !== id));
      if (selectedInfluencer?.id === id) {
        setSelectedInfluencer(null);
      }
    } catch (error) {
      console.error('Error removing influencer:', error);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedInfluencer) return;

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      const pricingData: Record<string, number> = {};
      Object.entries(editingData.pricing).forEach(([key, value]) => {
        const num = parseFloat(value);
        if (!isNaN(num) && num > 0) {
          pricingData[key] = num;
        }
      });

      const { error } = await supabase
        .from('organization_influencers')
        .update({
          first_name: editingData.first_name || null,
          last_name: editingData.last_name || null,
          address: editingData.address || null,
          siret: editingData.siret || null,
          date_of_birth: editingData.date_of_birth || null,
          pricing: pricingData,
          manager_name: editingData.manager_name || null,
          manager_email: editingData.manager_email || null,
          manager_phone: editingData.manager_phone || null,
          manager_company: editingData.manager_company || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedInfluencer.id);

      if (error) {
        console.error('Error saving data:', error);
        alert(`Erreur lors de l'enregistrement: ${error.message}`);
        throw error;
      }

      const updatedData = {
        ...editingData,
        pricing: pricingData
      };

      setOrganizationInfluencers(prev =>
        prev.map(inf =>
          inf.id === selectedInfluencer.id
            ? { ...inf, ...updatedData }
            : inf
        )
      );

      setSelectedInfluencer(prev => prev ? { ...prev, ...updatedData } : null);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const PostImage = ({ imageUrl, postType }: { imageUrl: string | null; postType: string }) => {
    const [imageError, setImageError] = useState(false);
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (!imageUrl) {
        setLoading(false);
        return;
      }

      setImageDataUrl(imageUrl);
      setLoading(false);
    }, [imageUrl]);

    if (!imageUrl || imageError) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-white/[0.04]">
          <div className="text-white/30 text-sm">Image unavailable</div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-white/[0.04]">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60"></div>
        </div>
      );
    }

    return (
      <>
        <img
          src={imageDataUrl || ''}
          alt="Post"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setImageError(true)}
        />
        {postType === 'reel' && (
          <div className="absolute top-3 right-3 bg-pink-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
            Reel
          </div>
        )}
      </>
    );
  };

  const handleViewInfluencer = async (orgInfluencer: OrganizationInfluencer) => {
    setSelectedInfluencer(orgInfluencer);
    setModalPlatformTab('instagram');
    setLoadingTiktokPosts(true);
    try {
      const [ttRes, ytRes] = await Promise.all([
        supabase.from('tiktok_posts').select('*').eq('influencer_id', orgInfluencer.influencer_id).order('timestamp', { ascending: false }).limit(15),
        supabase.from('youtube_videos').select('*').eq('influencer_id', orgInfluencer.influencer_id).order('published_at', { ascending: false }).limit(15),
      ]);
      setTiktokPosts(prev => ({ ...prev, [orgInfluencer.influencer_id]: ttRes.data || [] }));
      setYoutubeVideos(prev => ({ ...prev, [orgInfluencer.influencer_id]: ytRes.data || [] }));
    } finally {
      setLoadingTiktokPosts(false);
    }

    const pricingStrings: Record<string, string> = {};
    if (orgInfluencer.pricing) {
      Object.entries(orgInfluencer.pricing).forEach(([key, value]) => {
        pricingStrings[key] = value.toString();
      });
    }

    setEditingData({
      first_name: orgInfluencer.first_name || '',
      last_name: orgInfluencer.last_name || '',
      address: orgInfluencer.address || '',
      siret: orgInfluencer.siret || '',
      date_of_birth: orgInfluencer.date_of_birth || '',
      pricing: pricingStrings,
      manager_name: orgInfluencer.manager_name || '',
      manager_email: orgInfluencer.manager_email || '',
      manager_phone: orgInfluencer.manager_phone || '',
      manager_company: orgInfluencer.manager_company || ''
    });

    setLoadingCollabPosts(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('influencer_id', orgInfluencer.influencer_id)
        .eq('is_collaboration', true)
        .order('timestamp', { ascending: false })
        .limit(20);

      if (error) throw error;
      setCollabPosts(data || []);
    } catch (error) {
      console.error('Error loading collaboration posts:', error);
    } finally {
      setLoadingCollabPosts(false);
    }

    if (userProfile?.agency_id) {
      setLoadingOrgCollabs(true);
      try {
        const collabs = await getSupabaseDocuments<Collab>('collabs', userProfile.agency_id);

        const relatedCollabs = collabs.filter((collab: Collab) =>
          collab.influencer_ids?.includes(orgInfluencer.influencer_id) ||
          collab.influencers?.some((inf: any) => inf.id === orgInfluencer.influencer_id)
        );

        setOrganizationCollabs(relatedCollabs);
      } catch (error) {
        console.error('Error loading organization collaborations:', error);
      } finally {
        setLoadingOrgCollabs(false);
      }
    }
  };

  const getDisplayName = (influencer: InfluencerData) => {
    return influencer.display_name || influencer.name || influencer.username;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const handleShareInfluencerLink = (influencerId: string) => {
    const publicUrl = `${window.location.origin}/influencer/${influencerId}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 4000);
    }).catch(err => {
      console.error('Erreur lors de la copie du lien:', err);
    });
  };

  const filteredInfluencers = organizationInfluencers.filter(orgInf => {
    const influencer = orgInf.influencer;
    const searchLower = search.toLowerCase();
    const addedBy = orgInf.added_by_user_id;

    const matchesSearch = (
      getDisplayName(influencer).toLowerCase().includes(searchLower) ||
      influencer.username.toLowerCase().includes(searchLower) ||
      (influencer.category && influencer.category.toLowerCase().includes(searchLower))
    );

    const matchesMember = memberFilter === 'all' || addedBy === memberFilter;

    return matchesSearch && matchesMember;
  });

  const allMembers = Array.from(
    new Map(
      organizationInfluencers
        .filter(inf => inf.added_by_user_id && inf.added_by_name)
        .map(inf => [inf.added_by_user_id, { id: inf.added_by_user_id, name: inf.added_by_name }])
    ).values()
  );

  const influencersByMember = allMembers.map(member => ({
    ...member,
    influencers: filteredInfluencers.filter(inf => inf.added_by_user_id === member.id)
  })).filter(group => group.influencers.length > 0);

  const InfluencerCard = ({ orgInf, influencer }: { orgInf: OrganizationInfluencer; influencer: InfluencerData }) => {
    const tab = platformTab[orgInf.id] || 'instagram';
    const ttProfile = tiktokProfiles[orgInf.influencer_id];
    const ytProfile = youtubeProfiles[orgInf.influencer_id];
    const snapProfile = snapchatProfiles[orgInf.influencer_id];
    const hasTiktok = !!ttProfile;
    const hasYoutube = !!ytProfile;
    const hasSnapchat = !!snapProfile;
    const isScrapingTt = tiktokScraping[orgInf.id] || false;
    const isScrapingYt = youtubeScraping[orgInf.id] || false;
    const isScrapingSnap = snapchatScraping[orgInf.id] || false;
    const isRefreshingAll = refreshingAll[orgInf.id] || false;
    const hasAnyNetwork = hasTiktok || hasYoutube || hasSnapchat || (influencer?.platform === 'instagram' && !!influencer?.username);
    const showTtInput = showTiktokInput[orgInf.id] || false;
    const showYtInput = showYoutubeInput[orgInf.id] || false;
    const showSnapInput = showSnapchatInput[orgInf.id] || false;

    const displayFollowers = tab === 'tiktok' && ttProfile
      ? ttProfile.followers_count
      : tab === 'youtube' && ytProfile
        ? ytProfile.subscribers_count
        : tab === 'snapchat' && snapProfile
          ? snapProfile.subscriber_count
          : influencer.followers_count;
    const displayEngagement = tab === 'tiktok' && ttProfile
      ? ttProfile.engagement_rate
      : tab === 'youtube' && ytProfile
        ? ytProfile.engagement_rate
        : tab === 'snapchat'
          ? null
          : influencer.engagement_rate;
    const displayAvgViews = tab === 'tiktok' && ttProfile
      ? ttProfile.avg_views
      : tab === 'youtube' && ytProfile
        ? ytProfile.avg_views
        : null;
    const snapSnapsCount = snapchatSnaps[orgInf.influencer_id]?.length ?? 0;

    return (
      <>
        <div className="flex items-start gap-4 mb-4">
          <div className="relative flex-shrink-0">
            <InfluencerAvatar
              profileImageUrl={influencer.profile_image_url}
              name={getDisplayName(influencer)}
              size="lg"
            />
            {influencer.verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <Award className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white truncate text-lg">
              {getDisplayName(influencer)}
            </h3>
            <p className="text-sm text-white/50 truncate">
              @{influencer.username}
            </p>
            {hasTiktok && (
              <p className="text-xs text-[#00F2EA] font-medium truncate mt-0.5">
                @{ttProfile.tiktok_username} <TikTokIcon className="inline w-3 h-3 ml-0.5" />
              </p>
            )}
            {hasYoutube && (
              <p className="text-xs text-[#FF0000] font-medium truncate mt-0.5">
                {ytProfile.youtube_handle} <YouTubeIcon className="inline w-3 h-3 ml-0.5" />
              </p>
            )}
            {hasSnapchat && (
              <p className="text-xs text-[#FFFC00] font-medium truncate mt-0.5" style={{ textShadow: '0 0 8px rgba(255,252,0,0.4)' }}>
                @{snapProfile.username} <SnapchatIcon className="inline w-3 h-3 ml-0.5" />
              </p>
            )}
          </div>
        </div>

        {/* Platform toggle */}
        {(hasTiktok || hasYoutube || hasSnapchat) && (
          <div className="flex gap-1 mb-3 flex-wrap" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'instagram' }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                tab === 'instagram'
                  ? 'bg-gradient-to-r from-[#833AB4] to-[#E1306C] text-white shadow'
                  : 'bg-white/[0.06] text-white/40'
              }`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </button>
            {hasTiktok && (
              <button
                onClick={() => setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'tiktok' }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  tab === 'tiktok'
                    ? 'bg-black text-white shadow'
                    : 'bg-white/[0.06] text-white/40'
                }`}
              >
                <TikTokIcon className="w-3 h-3" />
                TikTok
              </button>
            )}
            {hasYoutube && (
              <button
                onClick={() => setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'youtube' }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  tab === 'youtube'
                    ? 'bg-[#FF0000] text-white shadow'
                    : 'bg-white/[0.06] text-white/40'
                }`}
              >
                <YouTubeIcon className="w-3.5 h-3.5" />
                YouTube
              </button>
            )}
            {hasSnapchat && (
              <button
                onClick={() => setPlatformTab(prev => ({ ...prev, [orgInf.id]: 'snapchat' }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  tab === 'snapchat'
                    ? 'bg-[#FFFC00] text-black shadow'
                    : 'bg-white/[0.06] text-white/40'
                }`}
              >
                <SnapchatIcon className="w-3.5 h-3.5" />
                Snapchat
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-white/[0.04] rounded-xl">
            <div className="text-[10px] text-white/40 mb-1.5 font-semibold uppercase">
              {tab === 'snapchat' ? 'Abonnés' : 'Followers'}
            </div>
            <div className="font-bold text-base text-white">
              {formatNumber(displayFollowers)}
            </div>
          </div>
          {tab === 'snapchat' ? (
            <div className="text-center p-3 col-span-2 bg-white/[0.04] rounded-xl">
              <div className="text-[10px] text-yellow-400 mb-1.5 font-semibold uppercase">Snaps récents</div>
              <div className="font-bold text-base text-yellow-300">
                {snapSnapsCount}
              </div>
            </div>
          ) : (
            <>
              <div className="text-center p-3 bg-white/[0.04] rounded-xl">
                <div className="text-[10px] text-white/40 mb-1.5 font-semibold uppercase">Engagement</div>
                <div className="font-bold text-base text-green-400">
                  {displayEngagement ? `${Number(displayEngagement).toFixed(1)}%` : <span className="text-xs text-white/30">N/A</span>}
                </div>
              </div>
              {(tab === 'tiktok' && ttProfile) || (tab === 'youtube' && ytProfile) ? (
                <div className="text-center p-3 bg-white/[0.04] rounded-xl">
                  <div className="text-[10px] text-white/40 mb-1.5 font-semibold uppercase">Moy. vues</div>
                  <div className="font-bold text-base text-blue-400">
                    {formatNumber(displayAvgViews || 0)}
                  </div>
                </div>
              ) : (
                <div className="text-center p-3 bg-white/[0.04] rounded-xl">
                  <div className="text-[10px] text-white/40 mb-1.5 font-semibold uppercase">Collabs</div>
                  <div className="font-bold text-base text-blue-400">
                    {orgInf.collabCount || 0}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {orgInf.rating && (
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className={`w-4 h-4 ${star <= orgInf.rating! ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}`} />
            ))}
          </div>
        )}

        {/* TikTok input */}
        {showTtInput && (
          <div className="mb-3" onClick={e => e.stopPropagation()}>
            <div className="flex gap-2">
              <input
                type="text"
                value={tiktokInputMap[orgInf.id] || ''}
                onChange={e => setTiktokInputMap(prev => ({ ...prev, [orgInf.id]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleScrapeTiktok(orgInf); }}
                placeholder="@handle ou lien TikTok"
                className="flex-1 px-3 py-2 text-sm rounded-lg border bg-white/[0.06] border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-[#00F2EA] outline-none"
                autoFocus
              />
              <button
                onClick={() => handleScrapeTiktok(orgInf)}
                disabled={isScrapingTt || !tiktokInputMap[orgInf.id]?.trim()}
                className="px-3 py-2 bg-black text-white rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-gray-900 transition-colors"
              >
                {isScrapingTt ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : 'OK'}
              </button>
              <button
                onClick={() => setShowTiktokInput(prev => ({ ...prev, [orgInf.id]: false }))}
                className="px-2 py-2 rounded-lg text-white/40 hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* YouTube input */}
        {showYtInput && (
          <div className="mb-3" onClick={e => e.stopPropagation()}>
            <div className="flex gap-2">
              <input
                type="text"
                value={youtubeInputMap[orgInf.id] || ''}
                onChange={e => setYoutubeInputMap(prev => ({ ...prev, [orgInf.id]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleScrapeYoutube(orgInf); }}
                placeholder="@handle ou lien YouTube"
                className="flex-1 px-3 py-2 text-sm rounded-lg border bg-white/[0.06] border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-[#FF0000] outline-none"
                autoFocus
              />
              <button
                onClick={() => handleScrapeYoutube(orgInf)}
                disabled={isScrapingYt || !youtubeInputMap[orgInf.id]?.trim()}
                className="px-3 py-2 bg-[#FF0000] text-white rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-red-700 transition-colors"
              >
                {isScrapingYt ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : 'OK'}
              </button>
              <button
                onClick={() => setShowYoutubeInput(prev => ({ ...prev, [orgInf.id]: false }))}
                className="px-2 py-2 rounded-lg text-white/40 hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Snapchat input */}
        {showSnapInput && (
          <div className="mb-3" onClick={e => e.stopPropagation()}>
            <div className="flex gap-2">
              <input
                type="text"
                value={snapchatInputMap[orgInf.id] || ''}
                onChange={e => setSnapchatInputMap(prev => ({ ...prev, [orgInf.id]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleScrapeSnapchat(orgInf); }}
                placeholder="username ou snapchat.com/add/..."
                className="flex-1 px-3 py-2 text-sm rounded-lg border bg-white/[0.06] border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFFC00] outline-none"
                autoFocus
              />
              <button
                onClick={() => handleScrapeSnapchat(orgInf)}
                disabled={isScrapingSnap || !snapchatInputMap[orgInf.id]?.trim()}
                className="px-3 py-2 bg-[#FFFC00] text-black rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-yellow-300 transition-colors"
              >
                {isScrapingSnap ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : 'OK'}
              </button>
              <button
                onClick={() => setShowSnapchatInput(prev => ({ ...prev, [orgInf.id]: false }))}
                className="px-2 py-2 rounded-lg text-white/40 hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2" onClick={e => e.stopPropagation()}>
          {/* Réseau sociaux + Profil */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                setShowTiktokInput(prev => ({ ...prev, [orgInf.id]: !prev[orgInf.id] }));
                setShowYoutubeInput(prev => ({ ...prev, [orgInf.id]: false }));
                setShowSnapchatInput(prev => ({ ...prev, [orgInf.id]: false }));
              }}
              className={`p-2 flex items-center gap-1 text-xs font-semibold rounded-lg transition-colors ${
                hasTiktok ? 'bg-[#00F2EA]/10 text-[#00F2EA] hover:bg-[#00F2EA]/20' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10] hover:text-[#00F2EA]'
              }`}
              title={hasTiktok ? 'Mettre à jour TikTok' : 'Ajouter TikTok'}
            >
              <TikTokIcon className="w-3.5 h-3.5" />
              {!hasTiktok && <Plus className="w-3 h-3" />}
            </button>
            <button
              onClick={() => {
                setShowYoutubeInput(prev => ({ ...prev, [orgInf.id]: !prev[orgInf.id] }));
                setShowTiktokInput(prev => ({ ...prev, [orgInf.id]: false }));
                setShowSnapchatInput(prev => ({ ...prev, [orgInf.id]: false }));
              }}
              className={`p-2 flex items-center gap-1 text-xs font-semibold rounded-lg transition-colors ${
                hasYoutube ? 'bg-[#FF0000]/10 text-[#FF0000] hover:bg-[#FF0000]/20' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10] hover:text-[#FF0000]'
              }`}
              title={hasYoutube ? 'Mettre à jour YouTube' : 'Ajouter YouTube'}
            >
              <YouTubeIcon className="w-3.5 h-3.5" />
              {!hasYoutube && <Plus className="w-3 h-3" />}
            </button>
            <button
              onClick={() => {
                setShowSnapchatInput(prev => ({ ...prev, [orgInf.id]: !prev[orgInf.id] }));
                setShowTiktokInput(prev => ({ ...prev, [orgInf.id]: false }));
                setShowYoutubeInput(prev => ({ ...prev, [orgInf.id]: false }));
              }}
              className={`p-2 flex items-center gap-1 text-xs font-semibold rounded-lg transition-colors ${
                hasSnapchat ? 'bg-[#FFFC00]/10 text-[#FFFC00] hover:bg-[#FFFC00]/20' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10] hover:text-[#FFFC00]'
              }`}
              title={hasSnapchat ? 'Mettre à jour Snapchat' : 'Ajouter Snapchat'}
            >
              <SnapchatIcon className="w-3.5 h-3.5" />
              {!hasSnapchat && <Plus className="w-3 h-3" />}
            </button>
            {influencer.profile_url && (
              <a
                href={influencer.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white/[0.06] text-white/50 hover:bg-white/[0.10] hover:text-white/80 border border-white/[0.08] rounded-lg transition-colors text-xs font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Profil
              </a>
            )}
          </div>
          {/* Actions */}
          <div className="flex items-center gap-1.5 pt-1 border-t border-white/[0.04]">
            <button
              onClick={() => handleShareInfluencerLink(influencer.id)}
              className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
              title="Partager le lien"
            >
              <Share2 className="w-4 h-4" />
            </button>
            {hasAnyNetwork && (
              <button
                onClick={() => handleRefreshAll(orgInf)}
                disabled={isRefreshingAll}
                className="p-2 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-50 rounded-lg transition-colors"
                title="Rafraîchir tous les réseaux"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshingAll ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={() => handleRemoveInfluencer(orgInf.id)}
              className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
              title="Retirer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-white/60"></div>
          <p className="mt-4 text-white/50">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-screen p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Créateurs
            </h1>
            <p className="text-sm text-white/50">
              {filteredInfluencers.length} influenceur{filteredInfluencers.length > 1 ? 's' : ''} dans votre organisation
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6">
        {/* View mode toggle */}
        <div className="flex items-center gap-2 mb-6 pb-6 border-b border-white/[0.06]">
          <button
            onClick={() => setViewMode('by-member')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'by-member'
                ? 'bg-white/[0.10] text-white border-2 border-white/20'
                : 'bg-white/[0.04] text-white/40 border-2 border-transparent hover:bg-white/[0.06]'
            }`}
          >
            <Folder className="w-4 h-4" />
            Par membre
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'all'
                ? 'bg-white/[0.10] text-white border-2 border-white/20'
                : 'bg-white/[0.04] text-white/40 border-2 border-transparent hover:bg-white/[0.06]'
            }`}
          >
            <Users className="w-4 h-4" />
            Tous
          </button>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            placeholder="Rechercher un influenceur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-12 pl-12 pr-4 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
          />
        </div>

        {filteredInfluencers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/[0.06] rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-white/30" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              Aucun influenceur dans votre organisation
            </h3>
            <p className="text-white/50">
              Allez dans la page Discover pour ajouter des influenceurs
            </p>
          </div>
        ) : viewMode === 'by-member' ? (
          <div className="space-y-8">
            {influencersByMember.map((group) => (
              <div key={group.id} className="space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b-2 border-white/[0.06]">
                  <div className="w-10 h-10 rounded-full bg-white/[0.08] flex items-center justify-center text-white font-bold shadow-md">
                    {group.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{group.name}</h3>
                    <p className="text-sm text-white/50">
                      {group.influencers.length} influenceur{group.influencers.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.influencers.map((orgInf) => (
                    <div
                      key={orgInf.id}
                      className="bg-white/[0.04] border-white/[0.08] hover:border-white/20 rounded-xl border p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                      onClick={() => handleViewInfluencer(orgInf)}
                    >
                      <InfluencerCard orgInf={orgInf} influencer={orgInf.influencer} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInfluencers.map((orgInf) => (
              <div
                key={orgInf.id}
                className="bg-white/[0.04] border-white/[0.08] hover:border-white/20 rounded-xl border p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => handleViewInfluencer(orgInf)}
              >
                <InfluencerCard orgInf={orgInf} influencer={orgInf.influencer} />
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedInfluencer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedInfluencer(null)}>
          <div
            className="bg-[#141414] border border-white/[0.08] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#141414] border-b border-white/[0.08] p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-white">
                {getDisplayName(selectedInfluencer.influencer)}
              </h2>
              <button
                onClick={() => setSelectedInfluencer(null)}
                className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white/40" />
              </button>
            </div>

            <div className="p-6 space-y-6">

              {/* Platform tabs in modal */}
              {(tiktokProfiles[selectedInfluencer.influencer_id] || youtubeProfiles[selectedInfluencer.influencer_id] || snapchatProfiles[selectedInfluencer.influencer_id]) && (
                <div className="flex gap-2 flex-wrap border-b border-white/[0.08] pb-4">
                  <button
                    onClick={() => setModalPlatformTab('instagram')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                      modalPlatformTab === 'instagram'
                        ? 'bg-gradient-to-r from-[#833AB4] to-[#E1306C] text-white shadow-md'
                        : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'
                    }`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    Instagram
                  </button>
                  {tiktokProfiles[selectedInfluencer.influencer_id] && (
                    <button
                      onClick={() => setModalPlatformTab('tiktok')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                        modalPlatformTab === 'tiktok'
                          ? 'bg-black text-white shadow-md'
                          : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'
                      }`}
                    >
                      <TikTokIcon className="w-4 h-4" />
                      TikTok
                    </button>
                  )}
                  {youtubeProfiles[selectedInfluencer.influencer_id] && (
                    <button
                      onClick={() => setModalPlatformTab('youtube')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                        modalPlatformTab === 'youtube'
                          ? 'bg-[#FF0000] text-white shadow-md'
                          : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'
                      }`}
                    >
                      <YouTubeIcon className="w-4 h-4" />
                      YouTube
                    </button>
                  )}
                  {snapchatProfiles[selectedInfluencer.influencer_id] && (
                    <button
                      onClick={() => setModalPlatformTab('snapchat')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                        modalPlatformTab === 'snapchat'
                          ? 'bg-[#FFFC00] text-black shadow-md'
                          : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'
                      }`}
                    >
                      <SnapchatIcon className="w-4 h-4" />
                      Snapchat
                    </button>
                  )}
                </div>
              )}

              {/* TikTok stats panel */}
              {modalPlatformTab === 'tiktok' && tiktokProfiles[selectedInfluencer.influencer_id] && (() => {
                const ttp = tiktokProfiles[selectedInfluencer.influencer_id];
                const ttv = tiktokPosts[selectedInfluencer.influencer_id] || [];
                return (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center shadow">
                        <TikTokIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-lg text-white">@{ttp.tiktok_username}</p>
                        <p className="text-xs text-white/40">
                          Mis à jour {new Date(ttp.scraped_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Abonnés', value: formatNumber(ttp.followers_count), color: 'gray' },
                        { label: 'Engagement', value: ttp.engagement_rate ? `${ttp.engagement_rate}%` : 'N/A', color: 'green' },
                        { label: 'Moy. vues', value: formatNumber(ttp.avg_views), color: 'blue' },
                        { label: 'Moy. likes', value: formatNumber(ttp.avg_likes), color: 'red' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="text-center p-4 rounded-xl bg-white/[0.04]">
                          <div className="text-[10px] font-semibold uppercase mb-1.5 text-white/40">{label}</div>
                          <div className="font-bold text-lg text-white">{value}</div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        Dernières vidéos TikTok ({ttv.length})
                      </h3>
                      {loadingTiktokPosts ? (
                        <div className="flex justify-center py-8">
                          <div className="w-8 h-8 border-4 border-white/10 border-t-white/60 rounded-full animate-spin" />
                        </div>
                      ) : ttv.length === 0 ? (
                        <p className="text-sm text-white/40 text-center py-6">Aucune vidéo disponible</p>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                          {ttv.map((video) => (
                            <a
                              key={video.id}
                              href={video.video_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04] transition-all hover:scale-[1.02] hover:shadow-lg"
                            >
                              <div className="aspect-[9/16] relative">
                                {video.thumbnail_url ? (
                                  <img
                                    src={video.thumbnail_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-black">
                                    <TikTokIcon className="w-8 h-8 text-white opacity-30" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                  <div className="flex items-center justify-between text-white text-[10px] font-semibold">
                                    <span className="flex items-center gap-0.5">
                                      <Eye className="w-3 h-3" />
                                      {formatNumber(video.views_count)}
                                    </span>
                                    <span className="flex items-center gap-0.5">
                                      <Heart className="w-3 h-3" />
                                      {formatNumber(video.likes_count)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* YouTube stats panel */}
              {modalPlatformTab === 'youtube' && youtubeProfiles[selectedInfluencer.influencer_id] && (() => {
                const ytp = youtubeProfiles[selectedInfluencer.influencer_id];
                const ytv = youtubeVideos[selectedInfluencer.influencer_id] || [];
                return (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#FF0000] flex items-center justify-center shadow">
                        <YouTubeIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-lg text-white">{ytp.channel_name || ytp.youtube_handle}</p>
                        <p className="text-xs text-white/40">
                          Mis à jour {new Date(ytp.scraped_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Abonnés', value: formatNumber(ytp.subscribers_count) },
                        { label: 'Engagement', value: ytp.engagement_rate ? `${ytp.engagement_rate}%` : 'N/A' },
                        { label: 'Moy. vues', value: formatNumber(ytp.avg_views) },
                        { label: 'Moy. likes', value: formatNumber(ytp.avg_likes) },
                      ].map(({ label, value }) => (
                        <div key={label} className="text-center p-4 rounded-xl bg-white/[0.04]">
                          <div className="text-[10px] font-semibold uppercase mb-1.5 text-white/40">{label}</div>
                          <div className="font-bold text-lg text-white">{value}</div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        Dernières vidéos YouTube ({ytv.length})
                      </h3>
                      {loadingTiktokPosts ? (
                        <div className="flex justify-center py-8">
                          <div className="w-8 h-8 border-4 border-white/10 border-t-white/60 rounded-full animate-spin" />
                        </div>
                      ) : ytv.length === 0 ? (
                        <p className="text-sm text-white/40 text-center py-6">Aucune vidéo disponible</p>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                          {ytv.map((video) => (
                            <a
                              key={video.id}
                              href={video.video_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04] transition-all hover:scale-[1.02] hover:shadow-lg"
                            >
                              <div className="aspect-video relative">
                                {video.thumbnail_url ? (
                                  <img
                                    src={video.thumbnail_url}
                                    alt={video.title || ''}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-[#FF0000]/10">
                                    <YouTubeIcon className="w-8 h-8 text-[#FF0000] opacity-50" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                  <div className="flex items-center justify-between text-white text-[10px] font-semibold">
                                    <span className="flex items-center gap-0.5">
                                      <Eye className="w-3 h-3" />
                                      {formatNumber(video.view_count)}
                                    </span>
                                    <span className="flex items-center gap-0.5">
                                      <Heart className="w-3 h-3" />
                                      {formatNumber(video.likes_count)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {video.title && (
                                <div className="p-1.5 text-[10px] text-white/40 line-clamp-2 leading-tight">
                                  {video.title}
                                </div>
                              )}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Snapchat stats panel */}
              {modalPlatformTab === 'snapchat' && snapchatProfiles[selectedInfluencer.influencer_id] && (() => {
                const snap = snapchatProfiles[selectedInfluencer.influencer_id];
                const stories = snapchatStories[selectedInfluencer.influencer_id] || [];
                const spotlights = snapchatSpotlights[selectedInfluencer.influencer_id] || [];
                const avgViews = spotlights.length > 0
                  ? Math.round(spotlights.reduce((s, sp) => s + (sp.views || 0), 0) / spotlights.length)
                  : 0;
                // legacy snaps kept for backwards compat
                const snaps = snapchatSnaps[selectedInfluencer.influencer_id] || [];
                const spotlightSnaps = snaps.filter(s => s.source === 'spotlight');
                const storySnaps = snaps.filter(s => s.source === 'story');
                const curatedSnaps = snaps.filter(s => s.source === 'curated');
                return (
                  <div className="space-y-6">
                    {/* Profile header */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {snap.profile_picture_url ? (
                          <img
                            src={snap.profile_picture_url}
                            alt={snap.name || snap.username}
                            className="w-16 h-16 rounded-2xl object-cover border-2 border-[#FFFC00]/30"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-2xl bg-[#FFFC00]/20 flex items-center justify-center">
                            <SnapchatIcon className="w-8 h-8 text-[#FFFC00]" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-lg text-white">
                          {snap.name || `@${snap.username}`}
                        </p>
                        <p className="text-[#FFFC00] text-sm font-medium">@{snap.username}</p>
                        {snap.bio && (
                          <p className="text-xs mt-1 line-clamp-2 text-white/40">{snap.bio}</p>
                        )}
                        <p className="text-xs mt-1 text-white/30">
                          Mis à jour {new Date(snap.scraped_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      {snap.snapcode_url && (
                        <img
                          src={snap.snapcode_url}
                          alt="Snapcode"
                          className="w-16 h-16 rounded-xl flex-shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-4 rounded-xl bg-white/[0.04]">
                        <div className="text-[10px] font-semibold uppercase mb-1.5 text-yellow-400">Abonnés</div>
                        <div className="font-bold text-lg text-white">{formatNumber(snap.subscriber_count)}</div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-white/[0.04]">
                        <div className="text-[10px] font-semibold uppercase mb-1.5 text-yellow-400">Vues moy.</div>
                        <div className="font-bold text-lg text-white">{avgViews > 0 ? formatNumber(avgViews) : '—'}</div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-white/[0.04]">
                        <div className="text-[10px] font-semibold uppercase mb-1.5 text-yellow-400">Spotlights</div>
                        <div className="font-bold text-lg text-white">{spotlights.length}</div>
                      </div>
                    </div>

                    {/* Stories section */}
                    {stories.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold flex items-center gap-2 text-white/70">
                          <SnapchatIcon className="w-4 h-4 text-[#FFFC00]" />
                          Stories récentes ({stories.length})
                        </h4>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                          {stories.map((story) => (
                            <div key={story.id} className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04]">
                              <div className="aspect-[9/16] relative">
                                {story.thumbnail_url ? (
                                  <img src={story.thumbnail_url} alt={story.story_title || ''} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-[#FFFC00]/10">
                                    <SnapchatIcon className="w-8 h-8 text-[#FFFC00] opacity-50" />
                                  </div>
                                )}
                                {story.story_title && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                                    <p className="text-white text-[9px] font-semibold truncate">{story.story_title}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Spotlights section */}
                    {spotlights.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold flex items-center gap-2 text-white/70">
                          <SnapchatIcon className="w-4 h-4 text-[#FFFC00]" />
                          Spotlights ({spotlights.length})
                        </h4>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                          {spotlights.map((sp) => (
                            <div key={sp.id} className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04]">
                              <div className="aspect-[9/16] relative">
                                {sp.thumbnail_url ? (
                                  <img src={sp.thumbnail_url} alt={sp.title || ''} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-[#FFFC00]/10">
                                    <SnapchatIcon className="w-8 h-8 text-[#FFFC00] opacity-50" />
                                  </div>
                                )}
                                {sp.views > 0 && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1.5">
                                    <div className="flex items-center gap-0.5 text-white text-[9px] font-semibold">
                                      <Eye className="w-2.5 h-2.5" />
                                      {formatNumber(sp.views)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Legacy snaps fallback */}
                    {stories.length === 0 && spotlights.length === 0 && snaps.length > 0 && (
                      <div className="space-y-4">
                        {[
                          { label: 'Stories', items: storySnaps },
                          { label: 'Spotlight', items: spotlightSnaps },
                          { label: 'Curated', items: curatedSnaps },
                        ].filter(g => g.items.length > 0).map(group => (
                          <div key={group.label}>
                            <h4 className="text-sm font-semibold mb-2 text-white/70 flex items-center gap-2">
                              <SnapchatIcon className="w-4 h-4 text-[#FFFC00]" />
                              {group.label} ({group.items.length})
                            </h4>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                              {group.items.map((s) => (
                                <a key={s.id} href={s.snap_url || '#'} target="_blank" rel="noopener noreferrer"
                                  className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04]"
                                  onClick={e => !s.snap_url && e.preventDefault()}>
                                  <div className="aspect-[9/16] relative">
                                    {s.thumbnail_url ? (
                                      <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-[#FFFC00]/10">
                                        <SnapchatIcon className="w-8 h-8 text-[#FFFC00] opacity-50" />
                                      </div>
                                    )}
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {modalPlatformTab === 'instagram' && (
              <><div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={editingData.first_name}
                    onChange={(e) => setEditingData({ ...editingData, first_name: e.target.value })}
                    className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                    placeholder="Prénom"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nom
                  </label>
                  <input
                    type="text"
                    value={editingData.last_name}
                    onChange={(e) => setEditingData({ ...editingData, last_name: e.target.value })}
                    className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                    placeholder="Nom"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </label>
                <input
                  type="text"
                  value={editingData.address}
                  onChange={(e) => setEditingData({ ...editingData, address: e.target.value })}
                  className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                  placeholder="Adresse complète"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  SIRET
                </label>
                <input
                  type="text"
                  value={editingData.siret}
                  onChange={(e) => setEditingData({ ...editingData, siret: e.target.value })}
                  className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                  placeholder="Numéro SIRET"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date de naissance
                </label>
                <input
                  type="date"
                  value={editingData.date_of_birth}
                  onChange={(e) => setEditingData({ ...editingData, date_of_birth: e.target.value })}
                  className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Tarifs par type de contenu
                </label>
                <div className="space-y-4">
                  {/* Instagram */}
                  <div className="bg-white/[0.04] rounded-xl p-4 border-2 border-[#E1306C]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F56040] flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                      <h4 className="font-semibold text-white">Instagram</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {['Story', 'Post', 'Reel'].map((type) => (
                        <div key={type}>
                          <label className="text-xs font-medium text-white/40 mb-1 block">
                            {type}
                          </label>
                          <input
                            type="number"
                            value={editingData.pricing[`instagram_${type.toLowerCase()}`] || ''}
                            onChange={(e) => setEditingData({
                              ...editingData,
                              pricing: { ...editingData.pricing, [`instagram_${type.toLowerCase()}`]: e.target.value }
                            })}
                            className="w-full p-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-lg focus:ring-1 focus:ring-[#E1306C] outline-none transition-all"
                            placeholder="€"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* TikTok */}
                  <div className="bg-white/[0.04] rounded-xl p-4 border-2 border-[#00F2EA]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center shadow-md">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" fill="white"/>
                        </svg>
                      </div>
                      <h4 className="font-semibold text-white">TikTok</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {['Video', 'Duo/Stitch'].map((type) => (
                        <div key={type}>
                          <label className="text-xs font-medium text-white/40 mb-1 block">
                            {type}
                          </label>
                          <input
                            type="number"
                            value={editingData.pricing[`tiktok_${type.toLowerCase().replace('/', '_')}`] || ''}
                            onChange={(e) => setEditingData({
                              ...editingData,
                              pricing: { ...editingData.pricing, [`tiktok_${type.toLowerCase().replace('/', '_')}`]: e.target.value }
                            })}
                            className="w-full p-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-lg focus:ring-1 focus:ring-[#00F2EA] outline-none transition-all"
                            placeholder="€"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* YouTube */}
                  <div className="bg-white/[0.04] rounded-xl p-4 border-2 border-[#FF0000]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#FF0000] flex items-center justify-center shadow-md">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </div>
                      <h4 className="font-semibold text-white">YouTube</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {['Short', 'Video', 'Intégration'].map((type) => (
                        <div key={type}>
                          <label className="text-xs font-medium text-white/40 mb-1 block">
                            {type}
                          </label>
                          <input
                            type="number"
                            value={editingData.pricing[`youtube_${type.toLowerCase()}`] || ''}
                            onChange={(e) => setEditingData({
                              ...editingData,
                              pricing: { ...editingData.pricing, [`youtube_${type.toLowerCase()}`]: e.target.value }
                            })}
                            className="w-full p-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-lg focus:ring-1 focus:ring-[#FF0000] outline-none transition-all"
                            placeholder="€"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Twitch */}
                  <div className="bg-white/[0.04] rounded-xl p-4 border-2 border-[#9146FF]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#9146FF] flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                        </svg>
                      </div>
                      <h4 className="font-semibold text-white">Twitch</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {['Stream', 'Intégration'].map((type) => (
                        <div key={type}>
                          <label className="text-xs font-medium text-white/40 mb-1 block">
                            {type}
                          </label>
                          <input
                            type="number"
                            value={editingData.pricing[`twitch_${type.toLowerCase()}`] || ''}
                            onChange={(e) => setEditingData({
                              ...editingData,
                              pricing: { ...editingData.pricing, [`twitch_${type.toLowerCase()}`]: e.target.value }
                            })}
                            className="w-full p-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-lg focus:ring-1 focus:ring-[#9146FF] outline-none transition-all"
                            placeholder="€"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.04] rounded-xl p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Référent / Manager
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/60 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={editingData.manager_name}
                      onChange={(e) => setEditingData({ ...editingData, manager_name: e.target.value })}
                      className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                      placeholder="Nom du manager"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white/60 mb-2">
                      Société
                    </label>
                    <input
                      type="text"
                      value={editingData.manager_company}
                      onChange={(e) => setEditingData({ ...editingData, manager_company: e.target.value })}
                      className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                      placeholder="Société"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={editingData.manager_email}
                      onChange={(e) => setEditingData({ ...editingData, manager_email: e.target.value })}
                      className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={editingData.manager_phone}
                      onChange={(e) => setEditingData({ ...editingData, manager_phone: e.target.value })}
                      className="w-full p-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleSaveNotes}
                disabled={isSaving}
                className={`w-full py-3 ${
                  saveSuccess
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-white text-black hover:bg-white/90'
                } rounded-xl font-semibold transition-colors disabled:opacity-50`}
              >
                {isSaving ? 'Enregistrement...' : saveSuccess ? 'Enregistré !' : 'Enregistrer'}
              </button>

              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Collaborations dans l'organisation ({organizationCollabs.length})
                </h3>

                {loadingOrgCollabs ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60"></div>
                  </div>
                ) : organizationCollabs.length === 0 ? (
                  <div className="text-center py-8 bg-white/[0.04] rounded-xl">
                    <p className="text-white/50">
                      Aucune collaboration dans l'organisation
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {organizationCollabs.map((collab) => (
                      <Link
                        key={collab.id}
                        to={`/app/collaborations/${collab.id}`}
                        className="block bg-white/[0.04] hover:bg-white/[0.06] rounded-xl p-4 transition-all group border border-white/[0.08]"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-white mb-1 group-hover:text-white/80 transition-colors">
                              {collab.name || 'Collaboration sans nom'}
                            </h4>
                            <div className="flex items-center gap-4 text-sm">
                              {collab.brand && (
                                <span className="text-white/50">
                                  {collab.brand}
                                </span>
                              )}
                              {collab.post_progress && collab.post_progress !== '0' && (
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  collab.post_progress === '100'
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {collab.post_progress === '100' ? 'Terminée' : `${collab.post_progress}%`}
                                </span>
                              )}
                            </div>
                            {collab.date && (
                              <div className="flex items-center gap-2 mt-2 text-xs text-white/30">
                                <Calendar className="w-3 h-3" />
                                {new Date(collab.date).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                          </div>
                          <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Collaborations détectées ({collabPosts.length})
                </h3>

                {loadingCollabPosts ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60"></div>
                  </div>
                ) : collabPosts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-white/50">
                      Aucune collaboration détectée
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {collabPosts.slice(0, 12).map((post) => (
                      <a
                        key={post.id}
                        href={post.post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white/[0.04] hover:bg-white/[0.06] rounded-xl overflow-hidden border border-white/[0.08] transition-all group"
                      >
                        <div className="aspect-square overflow-hidden relative">
                          <PostImage imageUrl={post.image_url} postType={post.post_type || ''} />
                        </div>
                        <div className="p-3">
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center gap-1">
                              <Heart className="w-4 h-4 text-red-400" />
                              <span className="text-white/50">
                                {post.likes_count.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MessageCircle className="w-4 h-4 text-blue-400" />
                              <span className="text-white/50">
                                {post.comments_count.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          {post.collaboration_keywords.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {post.collaboration_keywords.slice(0, 2).map((keyword, idx) => (
                                <span key={idx} className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
              </> )} {/* end modalPlatformTab === 'instagram' */}
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {showCopyNotification && (
        <div className="fixed bottom-8 right-8 z-50 animate-slideIn">
          <div className="bg-[#141414] rounded-lg shadow-2xl border border-white/[0.08] p-4 flex items-start gap-3 max-w-md">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-1">Lien copié !</h3>
              <p className="text-sm text-white/50">
                Partagez-le avec l'influenceur pour qu'il puisse voir ses collaborations avec votre agence.
              </p>
            </div>
            <button
              onClick={() => setShowCopyNotification(false)}
              className="text-white/30 hover:text-white/60 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
